import { useEffect, useState } from "react";
import { Loader2, Copy, Cloud, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const fmt = (n) => (Number(n) || 0).toLocaleString("cs-CZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " Kč";
const KIND = { bezna: "Faktura", zalohova: "Zálohová", dobropis: "Dobropis" };
const btn = "px-3 py-1.5 border border-slate-300 hover:bg-slate-50 rounded-sm uppercase tracking-wider font-semibold text-xs";

export function FakturyWebPicker({ onLoaded }) {
    const [rows, setRows] = useState(null);
    const [meta, setMeta] = useState({});
    const [sel, setSel] = useState(new Set());
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    const load = async () => {
        setBusy(true); setError("");
        try {
            const { data } = await api.get("/invoices/import/fakturyweb/list", { timeout: 120000 });
            setRows(data.invoices); setMeta({ supplier_ico: data.supplier_ico, skipped: data.skipped_received || [] }); setSel(new Set());
        } catch (e) { setError(e.response?.data?.detail || "Načtení seznamu z FakturyWeb selhalo"); }
        finally { setBusy(false); }
    };
    useEffect(() => { load(); }, []);

    const toggle = (code) => setSel((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n; });
    const selectAll = () => setSel(new Set((rows || []).filter((r) => !r.duplicate).map((r) => r.code)));

    const fetchSelected = async () => {
        if (!sel.size) { toast.error("Vyberte alespoň jednu fakturu"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/invoices/import/fakturyweb/fetch", { codes: Array.from(sel), supplier_ico: meta.supplier_ico || "" }, { timeout: 600000 });
            if (!data.count) toast.error("Z FakturyWeb se nepodařilo načíst žádnou fakturu");
            onLoaded(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Načtení faktur selhalo"); }
        finally { setBusy(false); }
    };

    if (error) return <div className="p-8 text-center text-sm text-red-700" data-testid="fw-error">{error}<div className="mt-4"><button onClick={load} className={btn}>Zkusit znovu</button></div></div>;
    if (!rows) return <div className="p-12 text-center" data-testid="fw-loading"><Loader2 className="w-10 h-10 mx-auto text-orange-600 animate-spin" /><div className="mt-4 text-sm text-slate-600">Načítám seznam faktur z FakturyWeb…</div></div>;

    const dups = rows.filter((r) => r.duplicate).length;
    return (
        <div className="flex flex-col flex-1 min-h-0">
            <div className="px-6 py-3 border-b border-slate-200 flex items-center gap-3 flex-wrap text-xs">
                <Cloud className="w-4 h-4 text-orange-600" />
                <span data-testid="fw-summary"><b>{rows.length}</b> faktur ve FakturyWeb · <b className="text-red-700">{dups}</b> už v aplikaci · vybráno <b className="text-emerald-700" data-testid="fw-selected-count">{sel.size}</b></span>
                <span className="flex-1" />
                <button onClick={selectAll} className={btn} data-testid="fw-select-all">Vybrat vše (bez duplicit)</button>
                <button onClick={() => setSel(new Set())} className={btn}>Zrušit výběr</button>
                <button onClick={load} className={btn} title="Obnovit"><RefreshCw className="w-3.5 h-3.5" /></button>
            </div>
            {meta.skipped?.length > 0 && <div className="px-6 py-2 bg-slate-50 text-slate-600 text-xs border-b border-slate-200" data-testid="fw-skipped-received">Přeskočeno {meta.skipped.reduce((a, s) => a + s.count, 0)} přijatých faktur od jiných dodavatelů ({meta.skipped.map((s) => `${s.supplier}: ${s.count}`).join(", ")}) – importují se jen faktury vystavené vaší firmou (IČO {meta.supplier_ico}).</div>}
            <div className="overflow-auto flex-1">
                <table className="w-full text-xs min-w-[800px]">
                    <thead className="sticky top-0 bg-slate-50">
                        <tr className="text-left text-[10px] uppercase tracking-widest text-slate-500 border-b border-slate-200">
                            <th className="px-3 py-2 w-8"></th><th className="px-3 py-2">Číslo</th><th className="px-3 py-2">Typ</th><th className="px-3 py-2">Odběratel</th>
                            <th className="px-3 py-2">Vystaveno</th><th className="px-3 py-2">Splatnost</th><th className="px-3 py-2 text-right">Celkem</th><th className="px-3 py-2">Úhrada</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={r.code} onClick={() => !r.duplicate && toggle(r.code)} data-testid={`fw-row-${i}`}
                                className={`border-b border-slate-100 ${r.duplicate ? "bg-red-50/60 opacity-60" : "cursor-pointer hover:bg-orange-50/40"} ${sel.has(r.code) ? "bg-orange-50" : ""}`}>
                                <td className="px-3 py-2"><input type="checkbox" checked={sel.has(r.code)} disabled={r.duplicate} onChange={() => toggle(r.code)} onClick={(e) => e.stopPropagation()} data-testid={`fw-check-${i}`} className="w-4 h-4 accent-orange-600" /></td>
                                <td className="px-3 py-2 font-mono font-semibold" data-testid={`fw-number-${i}`}>{r.number}{r.duplicate && <div className="text-[10px] text-red-700 inline-flex items-center gap-1 ml-2"><Copy className="w-3 h-3" /> Už existuje</div>}</td>
                                <td className="px-3 py-2 text-slate-500">{KIND[r.kind] || r.kind}</td>
                                <td className="px-3 py-2">{r.client_name}{r.client_ico && <span className="text-slate-400 ml-1">({r.client_ico})</span>}</td>
                                <td className="px-3 py-2">{r.issue_date}</td>
                                <td className="px-3 py-2">{r.due_date}</td>
                                <td className="px-3 py-2 text-right tabular-data font-semibold">{fmt(r.total)}</td>
                                <td className="px-3 py-2">{r.paid ? <span className="text-emerald-700 font-semibold">Uhrazeno {r.paid_at || ""}</span> : <span className="text-amber-700 font-semibold">Neuhrazeno</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div className="px-6 py-4 border-t-2 border-slate-900 flex items-center justify-between gap-2 flex-wrap">
                <div className="text-xs text-slate-500">Vybrané faktury se stáhnou i s originálním PDF a zobrazí se v kontrolní tabulce. Nic se zatím neukládá.</div>
                <button onClick={fetchSelected} disabled={busy || !sel.size} data-testid="fw-fetch" className="px-5 py-2 text-xs uppercase tracking-wider font-semibold bg-orange-600 hover:bg-orange-700 text-white rounded-sm disabled:opacity-50 inline-flex items-center gap-2">
                    {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Načíst vybrané ({sel.size})
                </button>
            </div>
        </div>
    );
}
